import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Clock3,
  FileText,
  Receipt,
  RefreshCw,
  Wallet,
} from "lucide-react";
import { getFinanceDashboard } from "../../api/finance/dashboard";
import { getAllAdvanceRequests } from "../../api/finance/advance/advanceRequests";
import { getAllPaymentRequests } from "../../api/finance/payment/paymentRequests";
import { getIncomeExpenditure } from "../../api/finance/incomeExpenditure";
import { getAllInvoices } from "../../api/finance/invoices/invoiceRequests";
import { hasPermission } from "../../utils/permissionUtils";
import {
  filterHubShortcuts,
  getFinanceHubItems,
  HUB_PREVIEW_LIMIT,
} from "./mobileNavConfig";
import {
  MobilePageShell,
  MobilePageHeader,
  MobileSectionHeader,
  MobileStatCard,
  MobileShortcutCard,
} from "./MobileNavPrimitives";

function formatCurrencyCompact(value) {
  const amount = Number(value || 0);
  const abs = Math.abs(amount);
  if (abs >= 1_000_000_000) {
    return `${(amount / 1_000_000_000).toLocaleString("vi-VN", {
      maximumFractionDigits: 1,
    })} tỷ`;
  }
  if (abs >= 1_000_000) {
    return `${(amount / 1_000_000).toLocaleString("vi-VN", {
      maximumFractionDigits: 1,
    })} triệu`;
  }
  return `${amount.toLocaleString("vi-VN")} ₫`;
}

function fmtDate(value) {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd/MM", { locale: vi });
  } catch {
    return "—";
  }
}

const SHORTCUT_TONES = {
  invoices: "indigo",
  salary: "emerald",
  payment_requests: "rose",
  advance: "amber",
  income_expenditure: "teal",
};

const KPI_KEY_BY_CARD = {
  waiting: "payment_requests",
  receipts: "income_expenditure",
  outflow: "payment_requests",
  advances: "advance",
};

const STATUS_LABEL = {
  waiting: "Chờ duyệt",
  approved: "Đã duyệt",
  rejected: "Từ chối",
  completed: "Hoàn tất",
  draft: "Nháp",
  submitted: "Đã gửi",
};

function sortByDateDesc(items, getDate) {
  return [...items].sort((a, b) => {
    const at = new Date(getDate(a) || 0).getTime();
    const bt = new Date(getDate(b) || 0).getTime();
    return bt - at;
  });
}

function JournalSection({ title, tone, action, children }) {
  return (
    <section>
      <MobileSectionHeader title={title} tone={tone} action={action} />
      {children}
    </section>
  );
}

function JournalList({ items, emptyText, renderItem }) {
  if (!items.length) {
    return (
      <p className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-[12px] text-gray-400">
        {emptyText}
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm divide-y divide-gray-50">
      {items.map(renderItem)}
    </div>
  );
}

export default function FinanceHubScreen() {
  const navigate = useNavigate();
  const hubItems = useMemo(() => getFinanceHubItems(), []);

  const canDash = hasPermission("finance.dashboard", "R");
  const canPayment = hasPermission("finance.payment_requests", "R");
  const canAdvance = hasPermission("finance.advance.access", "R");
  const canIncome = hasPermission("finance.income_expenditure", "R");
  const canInvoices = hasPermission("finance.invoices", "R");

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [waitingItems, setWaitingItems] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [recentAdvances, setRecentAdvances] = useState([]);
  const [recentCash, setRecentCash] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);

  const load = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      let nextStats = null;
      let nextPayments = [];
      let nextAdvances = [];
      let nextCash = [];
      let nextInvoices = [];
      let waitingPayments = [];
      let waitingAdvances = [];
      const jobs = [];

      if (canDash) {
        const now = new Date();
        jobs.push(
          getFinanceDashboard({
            year: now.getFullYear(),
            month: now.getMonth() + 1,
            branch_id: "all",
          })
            .then((data) => {
              nextStats = data?.stats || null;
            })
            .catch(() => {})
        );
      }

      if (canPayment) {
        jobs.push(
          getAllPaymentRequests()
            .then((records) => {
              const list = Array.isArray(records) ? records : [];
              nextPayments = sortByDateDesc(
                list,
                (x) => x.created_at || x.request_date
              ).slice(0, HUB_PREVIEW_LIMIT);
              waitingPayments = list
                .filter((x) => x.status === "waiting")
                .map((x) => ({
                  ...x,
                  _kind: "payment",
                  _amount: x.payment_amount,
                  _code: x.request_code || x.payment_code,
                  _title: x.request_code || x.payment_code || "Thanh toán",
                  _href: "/finance/payment_requests",
                }));
            })
            .catch(() => {})
        );
      }

      if (canAdvance) {
        jobs.push(
          getAllAdvanceRequests()
            .then((records) => {
              const list = Array.isArray(records) ? records : [];
              nextAdvances = sortByDateDesc(
                list,
                (x) => x.created_at || x.request_date
              ).slice(0, HUB_PREVIEW_LIMIT);
              waitingAdvances = list
                .filter((x) => x.status === "waiting")
                .map((x) => ({
                  ...x,
                  _kind: "advance",
                  _amount: x.advance_amount,
                  _code: x.request_code,
                  _title: x.request_code || "Tạm ứng",
                  _href: "/finance/advance",
                }));
            })
            .catch(() => {})
        );
      }

      if (canIncome) {
        const now = new Date();
        const from_date = new Date(now.getFullYear(), now.getMonth(), 1)
          .toISOString()
          .slice(0, 10);
        const to_date = new Date(now.getFullYear(), now.getMonth() + 1, 0)
          .toISOString()
          .slice(0, 10);
        jobs.push(
          getIncomeExpenditure({
            from_date,
            to_date,
            page: 1,
            pageSize: HUB_PREVIEW_LIMIT,
          })
            .then((res) => {
              nextCash = Array.isArray(res?.ledger?.records)
                ? res.ledger.records.slice(0, HUB_PREVIEW_LIMIT)
                : [];
            })
            .catch(() => {})
        );
      }

      if (canInvoices) {
        jobs.push(
          getAllInvoices({ page: 1, pageSize: HUB_PREVIEW_LIMIT })
            .then((res) => {
              nextInvoices = Array.isArray(res?.records)
                ? res.records.slice(0, HUB_PREVIEW_LIMIT)
                : [];
            })
            .catch(() => {})
        );
      }

      await Promise.all(jobs);

      const nextWaiting = sortByDateDesc(
        [...waitingPayments, ...waitingAdvances],
        (x) => x.created_at || x.request_date
      ).slice(0, HUB_PREVIEW_LIMIT);

      setStats(nextStats);
      setWaitingItems(nextWaiting);
      setRecentPayments(nextPayments);
      setRecentAdvances(nextAdvances);
      setRecentCash(nextCash);
      setRecentInvoices(nextInvoices);
      setLoading(false);
      setRefreshing(false);
    },
    [canDash, canPayment, canAdvance, canIncome, canInvoices]
  );

  useEffect(() => {
    load();
  }, [load]);

  const kpiCards = useMemo(() => {
    const cards = [];
    const s = stats;
    if (!s) return cards;

    if (canPayment || canAdvance) {
      cards.push({
        key: "waiting",
        label: "Chờ duyệt",
        value: String(s.approvals?.waitingCount ?? waitingItems.length),
        hint: `${s.approvals?.waitingPayments ?? 0} TT · ${
          s.approvals?.waitingAdvances ?? 0
        } TU`,
        icon: Clock3,
        tone: "amber",
        href: canPayment ? "/finance/payment_requests" : "/finance/advance",
      });
    }
    if (canIncome) {
      cards.push({
        key: "receipts",
        label: "Thực thu tháng",
        value: formatCurrencyCompact(s.receipts?.monthAmount),
        hint:
          s.receipts?.monthCount != null
            ? `${s.receipts.monthCount} phiếu`
            : "Thu trong tháng",
        icon: ArrowUpCircle,
        tone: "emerald",
        href: "/finance/income_expenditure",
      });
    }
    if (canPayment) {
      cards.push({
        key: "outflow",
        label: "Chi đã duyệt",
        value: formatCurrencyCompact(s.outflow?.monthAmount),
        hint: "Thanh toán + tạm ứng",
        icon: ArrowDownCircle,
        tone: "rose",
        href: "/finance/payment_requests",
      });
    }
    if (canAdvance) {
      cards.push({
        key: "advances",
        label: "Tạm ứng hoàn tất",
        value: String(s.advances?.completedMonthCount ?? 0),
        hint: "Trong tháng",
        icon: Wallet,
        tone: "teal",
        href: "/finance/advance",
      });
    }

    return cards.slice(0, 4);
  }, [stats, canPayment, canAdvance, canIncome, waitingItems.length]);

  const shortcuts = useMemo(() => {
    const exclude = new Set();
    for (const c of kpiCards) {
      if (c.key === "waiting") {
        if (canPayment) exclude.add("payment_requests");
        if (canAdvance) exclude.add("advance");
      } else if (c.key === "receipts" || c.key === "outflow") {
        exclude.add(KPI_KEY_BY_CARD[c.key]);
      } else if (c.key === "advances") {
        exclude.add("advance");
      }
    }
    return filterHubShortcuts(hubItems, [...exclude]);
  }, [hubItems, kpiCards, canPayment, canAdvance]);

  const showJournalBlock =
    canPayment || canAdvance || canIncome || canInvoices;

  const hasJournalContent =
    recentPayments.length > 0 ||
    recentAdvances.length > 0 ||
    recentCash.length > 0 ||
    recentInvoices.length > 0;

  return (
    <MobilePageShell>
      <MobilePageHeader
        title="Tài chính"
        subtitle="Tạm ứng, thanh toán, thu-chi…"
        tone="teal"
        trailing={
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing || loading}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-teal-100 bg-teal-50 text-teal-700 active:bg-teal-100 disabled:opacity-50"
            aria-label="Làm mới"
          >
            <RefreshCw
              size={16}
              className={refreshing ? "animate-spin" : undefined}
            />
          </button>
        }
      />

      <div className="space-y-5 px-5 pt-5 pb-4">
        {(canDash || kpiCards.length > 0) && (
          <section>
            <MobileSectionHeader title="Tình hình tài chính" tone="teal" />
            {loading ? (
              <div className="grid grid-cols-2 gap-3">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-[100px] animate-pulse rounded-2xl bg-teal-100/50"
                  />
                ))}
              </div>
            ) : kpiCards.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {kpiCards.map((card) => (
                  <MobileStatCard
                    key={card.key}
                    label={card.label}
                    value={card.value}
                    hint={card.hint}
                    icon={card.icon}
                    tone={card.tone}
                    onClick={() => navigate(card.href)}
                  />
                ))}
              </div>
            ) : (
              <p className="rounded-2xl border border-dashed border-teal-200 bg-teal-50/40 px-4 py-8 text-center text-[12px] text-gray-400">
                Chưa có chỉ số tài chính
              </p>
            )}
          </section>
        )}

        {!loading && waitingItems.length > 0 ? (
          <JournalSection
            title="Cần bạn xử lý"
            tone="rose"
            action={
              <button
                type="button"
                onClick={() =>
                  navigate(
                    canPayment
                      ? "/finance/payment_requests"
                      : "/finance/advance"
                  )
                }
                className="text-[12px] font-medium text-rose-700"
              >
                Xử lý
              </button>
            }
          >
            <JournalList
              items={waitingItems}
              emptyText="Không có đề nghị đang chờ duyệt"
              renderItem={(row) => (
                <button
                  key={`${row._kind}-${row.id}`}
                  type="button"
                  onClick={() => navigate(row._href)}
                  className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-gray-50"
                >
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-700">
                    <Clock3 size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-800">
                      {row._title}
                    </p>
                    <p className="mt-0.5 truncate text-[11px] text-gray-400">
                      {[
                        row._kind === "payment" ? "Thanh toán" : "Tạm ứng",
                        row.employee_name || row.requester_name,
                        formatCurrencyCompact(row._amount),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  <span className="mt-1 shrink-0 text-[10px] font-medium text-gray-400">
                    {fmtDate(row.created_at || row.request_date)}
                  </span>
                </button>
              )}
            />
          </JournalSection>
        ) : null}

        {shortcuts.length > 0 ? (
          <section>
            <MobileSectionHeader title="Lối tắt" tone="indigo" />
            <div className="grid grid-cols-2 gap-3">
              {shortcuts.map((item) => (
                <MobileShortcutCard
                  key={item.key}
                  icon={item.icon}
                  label={item.label}
                  tone={SHORTCUT_TONES[item.key] || "teal"}
                  onClick={() => navigate(`/finance/${item.key}`)}
                />
              ))}
            </div>
          </section>
        ) : null}

        {showJournalBlock ? (
          <section className="space-y-5">
            <MobileSectionHeader title="Nhật ký gần nhất" tone="amber" />
            <p className="-mt-1 pl-3 text-[11px] text-gray-500">
              Đọc nhanh đề nghị, thu-chi và hóa đơn theo quyền của bạn
            </p>

            {loading ? (
              <div className="space-y-3">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-28 animate-pulse rounded-2xl bg-amber-100/40"
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-5">
                {canPayment ? (
                  <JournalSection
                    title="Nhật ký thanh toán gần nhất"
                    tone="rose"
                    action={
                      <button
                        type="button"
                        onClick={() => navigate("/finance/payment_requests")}
                        className="text-[12px] font-medium text-rose-700"
                      >
                        Xem thêm
                      </button>
                    }
                  >
                    <JournalList
                      items={recentPayments}
                      emptyText="Chưa có đề nghị thanh toán gần đây"
                      renderItem={(row) => (
                        <button
                          key={row.id}
                          type="button"
                          onClick={() => navigate("/finance/payment_requests")}
                          className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-gray-50"
                        >
                          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-700">
                            <Receipt size={16} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-gray-800">
                              {row.request_code || row.payment_code || "Thanh toán"}
                            </p>
                            <p className="mt-0.5 truncate text-[11px] text-gray-400">
                              {[
                                STATUS_LABEL[row.status] || row.status,
                                row.employee_name || row.requester_name,
                                formatCurrencyCompact(row.payment_amount),
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          </div>
                          <span className="mt-1 shrink-0 text-[10px] font-medium text-gray-400">
                            {fmtDate(row.created_at || row.request_date)}
                          </span>
                        </button>
                      )}
                    />
                  </JournalSection>
                ) : null}

                {canAdvance ? (
                  <JournalSection
                    title="Nhật ký tạm ứng gần nhất"
                    tone="teal"
                    action={
                      <button
                        type="button"
                        onClick={() => navigate("/finance/advance")}
                        className="text-[12px] font-medium text-teal-700"
                      >
                        Xem thêm
                      </button>
                    }
                  >
                    <JournalList
                      items={recentAdvances}
                      emptyText="Chưa có đề nghị tạm ứng gần đây"
                      renderItem={(row) => (
                        <button
                          key={row.id}
                          type="button"
                          onClick={() => navigate("/finance/advance")}
                          className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-gray-50"
                        >
                          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                            <Wallet size={16} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-gray-800">
                              {row.request_code || "Tạm ứng"}
                            </p>
                            <p className="mt-0.5 truncate text-[11px] text-gray-400">
                              {[
                                STATUS_LABEL[row.status] || row.status,
                                row.employee_name,
                                formatCurrencyCompact(row.advance_amount),
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          </div>
                          <span className="mt-1 shrink-0 text-[10px] font-medium text-gray-400">
                            {fmtDate(row.created_at || row.request_date)}
                          </span>
                        </button>
                      )}
                    />
                  </JournalSection>
                ) : null}

                {canIncome ? (
                  <JournalSection
                    title="Nhật ký thu-chi gần nhất"
                    tone="emerald"
                    action={
                      <button
                        type="button"
                        onClick={() => navigate("/finance/income_expenditure")}
                        className="text-[12px] font-medium text-emerald-700"
                      >
                        Sổ cái
                      </button>
                    }
                  >
                    <JournalList
                      items={recentCash}
                      emptyText="Chưa có giao dịch thu-chi trong tháng"
                      renderItem={(row, idx) => (
                        <button
                          key={`${row.id || row.code}-${idx}`}
                          type="button"
                          onClick={() => navigate("/finance/income_expenditure")}
                          className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-gray-50"
                        >
                          <div
                            className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                              row.direction === "in"
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-rose-50 text-rose-700"
                            }`}
                          >
                            {row.direction === "in" ? (
                              <ArrowUpCircle size={16} />
                            ) : (
                              <ArrowDownCircle size={16} />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-gray-800">
                              {row.counterparty ||
                                row.description ||
                                row.code ||
                                "Giao dịch"}
                            </p>
                            <p className="mt-0.5 truncate text-[11px] text-gray-400">
                              {[
                                row.direction === "in" ? "Thu" : "Chi",
                                row.branch_name,
                                `${row.direction === "in" ? "+" : "−"}${formatCurrencyCompact(
                                  row.amount
                                )}`,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          </div>
                          <span className="mt-1 shrink-0 text-[10px] font-medium text-gray-400">
                            {fmtDate(row.entry_date || row.created_at)}
                          </span>
                        </button>
                      )}
                    />
                  </JournalSection>
                ) : null}

                {canInvoices ? (
                  <JournalSection
                    title="Nhật ký hóa đơn gần nhất"
                    tone="indigo"
                    action={
                      <button
                        type="button"
                        onClick={() => navigate("/finance/invoices")}
                        className="text-[12px] font-medium text-indigo-700"
                      >
                        Xem thêm
                      </button>
                    }
                  >
                    <JournalList
                      items={recentInvoices}
                      emptyText="Chưa có hóa đơn gần đây"
                      renderItem={(row) => (
                        <button
                          key={row.id}
                          type="button"
                          onClick={() => navigate("/finance/invoices")}
                          className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-gray-50"
                        >
                          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700">
                            <FileText size={16} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-gray-800">
                              {row.invoice_number || "Hóa đơn"}
                            </p>
                            <p className="mt-0.5 truncate text-[11px] text-gray-400">
                              {[
                                row.invoice_type === "purchase"
                                  ? "Mua vào"
                                  : "Bán ra",
                                row.partner_name || row.customer_name,
                                formatCurrencyCompact(row.total_amount),
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          </div>
                          <span className="mt-1 shrink-0 text-[10px] font-medium text-gray-400">
                            {fmtDate(row.invoice_date || row.created_at)}
                          </span>
                        </button>
                      )}
                    />
                  </JournalSection>
                ) : null}

                {!hasJournalContent ? (
                  <p className="rounded-2xl border border-dashed border-amber-200 bg-amber-50/40 px-4 py-8 text-center text-[12px] text-gray-400">
                    Chưa có nhật ký để hiển thị theo quyền hiện tại
                  </p>
                ) : null}
              </div>
            )}
          </section>
        ) : null}

        {!loading &&
        kpiCards.length === 0 &&
        shortcuts.length === 0 &&
        !showJournalBlock ? (
          <p className="py-12 text-center text-[12px] text-gray-400">
            Không có chức năng tài chính khả dụng
          </p>
        ) : null}
      </div>
    </MobilePageShell>
  );
}
